import React, { useEffect, useState } from "react"
import { Button, Card, Col, Row, Stack, Table } from "react-bootstrap"
import PageTitleButton from "../../components/PageTitleButton"
import { useParams } from "react-router-dom"
import axiosProvider from "../../lib/AxiosProvider"
import { admin } from "../../config/apiEndpoints"
import {
  getStatusClassName,
  HasModulePermission,
  useHandleUnauthorizedAccess,
} from "../../lib/GlobalMethods"
import { toast } from "react-toastify"
import InfiniteScroll from "react-infinite-scroll-component"
import BookingManagementSkeleton from "../../skeleton/bookingManagement"
import TooltipComponent from "../../components/Tooltip"
import { modulePermission } from "../../lib/GlobalVariables"
import moment from "moment"
import recordNotFound from "../../assets/images/record_not_found.png"
import BookingDetails from "../bookingManagement/BookingDetails"
import BookingCancellation from "../bookingManagement/BookingCancellation"

const ChargingStationDetails = () => {
  const canDelete = HasModulePermission(modulePermission.booking, "delete")
  const { id } = useParams()
  const [data, setData] = useState([])
  const [bookingData, setBookingData] = useState(null)
  const [show, setShow] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isView, setIsView] = useState(false)
  const [isUpdate, setIsUpdate] = useState(false)
  const { handleAccess } = useHandleUnauthorizedAccess()
  const [hasMore, setHasMore] = useState(true)
  const [cancellation, setCancellation] = useState(false)
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: false,
  })
  const handleShow = () => setShow(true)

  const fetchData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.chargingStationList}?station_id=${id}`,
      })
      setData(response?.data?.data)
      handleAccess({ code: response?.data?.code })
      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error(error.message || "An error occurred")
    } finally {
      setLoading(false)
    }
  }

  const fetchBookingdata = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.bookingList}?page=${
          pagination.current_page
        }&station_id=${id}`,
      })

      const total_pages = response?.data?.pagination?.total_pages

      if (response?.data?.code === 200) {
        setBookingData((prev) =>
          pagination.current_page === 1
            ? response?.data?.data
            : [...(prev ?? []), ...response.data.data],
        )
        setPagination((prev) => ({ ...prev, total_pages }))
        setHasMore(pagination.current_page < total_pages)
      }
    } catch (error) {
      setHasMore(false)
      console.error(error)
    }
  }

  const fetchMoreData = () => {
    if (pagination.current_page < pagination.total_pages) {
      setPagination((prevState) => ({
        ...prevState,
        current_page: prevState.current_page + 1,
      }))
    } else {
      setHasMore(false)
    }
  }

  const handleClose = () => {
    setShow(false)
    setCancellation(false)
  }

  useEffect(() => {
    fetchData()
    fetchBookingdata()
  }, [pagination.current_page])

  return data?.length ? (
    <>
      <PageTitleButton leftArrow={true} PagetitleText={data[0].station_name} />

      <Row className="mb-4">
        <Col xs="auto">
          <Card className="cmtcard_vehicle ctmcard_shadow">
            <Card.Body>
              <p className="text-primary mb-0 fw-medium">Bookings</p>
              <span className="text-black fw-medium">
                {data[0]?.booking_count ?? 0}
              </span>
            </Card.Body>
          </Card>
        </Col>
        <Col xs="auto">
          <Card className="cmtcard_vehicle ctmcard_shadow">
            <Card.Body>
              <p className="text-primary mb-0 fw-medium">Consumption</p>
              <span className="text-black fw-medium">
                {data[0]?.total_consumption?.toFixed(2) ?? 0}W
              </span>
            </Card.Body>
          </Card>
        </Col>
        <Col xs="auto">
          <Card className="cmtcard_vehicle ctmcard_shadow">
            <Card.Body>
              <p className="text-primary mb-0 fw-medium">Revenue</p>
              <span className="text-black fw-medium">
                {data[0]?.total_revenue?.toFixed(2) ?? 0}₹
              </span>
            </Card.Body>
          </Card>
        </Col>
        <Col xs="auto">
          <Card className="cmtcard_vehicle ctmcard_shadow">
            <Card.Body>
              <p className="text-primary mb-0 fw-medium">Nos.of Ports</p>
              <span className="text-black fw-medium">
                {data[0]?.chargers?.reduce(
                  (acc, charger) => acc + (charger.connectors?.length || 0),
                  0,
                ) ?? 0}
              </span>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={6}>
          <Card>
            <Table className="rounded mb-0 table-striped" responsive="md">
              <tbody>
                <tr>
                  <td className="text-nowrap text-secondary">Station Name :</td>
                  <td>
                    <span className="text-primary fw-medium">
                      {data[0].station_name}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="text-nowrap text-secondary">Address :</td>
                  <td>{data[0].address}</td>
                </tr>
                <tr>
                  <td className="text-nowrap text-secondary">Amenities :</td>
                  <td>
                    <Stack direction="horizontal" gap={3}>
                      {data[0].amenities?.length > 0 ? (
                        data[0].amenities.map((amenity, indexe) => (
                          <div className="csmenities_wrapper">
                            <img
                              className="csmenities_img"
                              src={
                                process.env.REACT_APP_RESOURCE_URL +
                                amenity.amenity_img
                              }
                              alt={amenity.amenity_name}
                            />
                            <span className="csmenities_name">
                              {amenity.amenity_name}
                            </span>
                          </div>
                        ))
                      ) : (
                        <></>
                      )}
                    </Stack>
                  </td>
                </tr>
                <tr>
                  <td className="text-nowrap text-secondary">Lat/Long :</td>
                  <td>
                    <Stack direction="horizontal" gap={2}>
                      <div className="flex-fill border-1 border-bottom border-end border-start border-top col-6 rounded-1 text-center py-1">
                        {data[0]?.location[0]?.latitude}
                      </div>
                      <div className="flex-fill border-1 border-bottom border-end border-start border-top col-6 rounded-1 text-center py-1">
                        {data[0]?.location[0]?.longitude}
                      </div>
                    </Stack>
                  </td>
                </tr>

                <tr>
                  <td className="text-nowrap text-secondary">Maintainance :</td>
                  <td>
                    Last on{" "}
                    {new Date(data[0]?.createdAt).toLocaleDateString("en-GB")}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>
        <Col md={6}>
          <iframe
            className="cs_iframe w-100 h-100 rounded-3 border"
            title="Maps Location"
            src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${data[0]?.location[0]?.latitude},${data[0]?.location[0]?.longitude}&zoom=16&maptype=roadmap`}
            loading="lazy"
          ></iframe>
        </Col>
      </Row>

      <h6 className="text-body-secondary">Chargers</h6>
      <Row className="row-gap-4">
        {data[0].chargers.map((item) => (
          <Col md={6} lg={4}>
            <Card className="h-100">
              <Card.Body>
                <Stack
                  direction="horizontal"
                  gap={3}
                  className="justify-content-between align-items-start"
                >
                  <div className="chrtitlestatus_wrapper">
                    <h3 className="chr_title">{item.id}</h3>
                    <div className="d-flex badegsstatus_action">
                      {item.connectors.map((data) => (
                        <span
                          className={`badges ${
                            data?.status === "Available"
                              ? "badges_success"
                              : "badges_danger"
                          } px-3 py-1`}
                        >
                          {data.connector_type}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="csvcntcors_imgs d-flex gap-2 flex-shrink-0">
                    {item.connectors.map((list) => (
                      <img
                        src={
                          list.connector_img
                            ? process.env.REACT_APP_RESOURCE_URL +
                              list.connector_img
                            : "https://placehold.co/50x50.png"
                        }
                        alt="Connector Imag"
                      />
                    ))}
                  </div>
                </Stack>
                <hr className="hr border-dashed" />
                <Stack
                  direction="horizontal"
                  className="align-items-start justify-content-between"
                  gap={1}
                >
                  <div className="vehicle_features">
                    <h4 className="vehiclefeature_title">Volatge</h4>
                    <h6 className="vehiclefeature_value">
                      {item.output_voltage}V
                    </h6>
                  </div>
                  <div className="vr" />
                  <div className="vehicle_features">
                    <h4 className="vehiclefeature_title">Current</h4>

                    <h6 className="vehiclefeature_value">
                      {" "}
                      {item.output_current}A
                    </h6>
                  </div>
                  <div className="vr" />
                  <div className="vehicle_features">
                    <h4 className="vehiclefeature_title">Firmware Version</h4>
                    <h6 className="vehiclefeature_value">
                      <span className="vehiclefeature_unit">
                        {" "}
                        {item.firmware_version}
                      </span>
                    </h6>
                  </div>
                </Stack>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      <div className="my-3">
        <h6 className="text-body-secondary">Booking List</h6>
        {bookingData !== null ? (
          <Card>
            <Card.Body>
              <InfiniteScroll
                dataLength={bookingData?.length || 10}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                  <BookingManagementSkeleton infiniteScroll={true} rows={1} />
                }
              >
                <Table
                  className="rounded mb-0 table-striped overflow-x-auto"
                  responsive
                >
                  <thead>
                    <tr>
                      <th className="text-center">Id</th>
                      <th className="text-center">Serial Number</th>
                      <th>User Details</th>
                      <th className="text-center">Station Name</th>
                      <th className="text-center">Connector Type</th>
                      <th className="text-center">Status</th>
                      <th className="text-center lh-1">Slot time</th>
                      <th className="text-center">Amount</th>
                      <th className="text-center">Payment method</th>
                      {canDelete && <th className="text-center">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {bookingData?.length > 0 &&
                      bookingData?.map((item, index) => (
                        <tr key={index + 1}>
                          <td
                            className="text-center text-primary"
                            onClick={() => {
                              handleShow()
                              setIsView(item)
                            }}
                            role="button"
                            tabIndex="0"
                          >
                            {item?.id}
                          </td>
                          <td className="text-center">
                            <p className="mb-0 lh-sm fs_14">
                              {item?.charger?.id}
                            </p>
                          </td>
                          <td>
                            <p className="mb-0 lh-1 text-nowrap">
                              {item?.user?.name}
                            </p>
                            <span className="fs_14 text_7gray text-nowrap">
                              {item?.user?.phone}
                            </span>
                          </td>
                          <td className="text-center">
                            <p className="mb-0 lh-sm fs_14">
                              {item?.station?.station_name}
                            </p>
                          </td>
                          <td>
                            <div className="sppted_connectors">
                              <div className="connector_item position-relative">
                                <span
                                  className="bg-body border border-opacity-75 d-inline-flex justify-content-center align-items-center fs_14 p-1 rounded-circle text-body text-uppercase position-absolute end-0"
                                  style={{ width: "22px", height: "22px" }}
                                >
                                  {item?.charger_position
                                    ?.charAt(0)
                                    .toUpperCase()}
                                </span>
                                <img
                                  src={
                                    item?.connector?.connector_img
                                      ? process.env.REACT_APP_RESOURCE_URL +
                                        item?.connector?.connector_img
                                      : "https://placehold.co/50x50.png"
                                  }
                                  alt="connector_img"
                                  className="connector_item_img"
                                />
                                <span className="connector_nm">
                                  {item?.connector?.connector_type}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="text-center">
                            <span
                              className={`badges ${getStatusClassName(
                                item.status,
                              )} fs_14 fw-normal px-3 py-1`}
                            >
                              {item?.status}
                            </span>
                          </td>
                          <td className="text-center fs_14">
                            <p className="mb-0 text_7gray lh-1">
                              {moment(item?.booked_start_time).format(
                                "YYYY-MM-DD",
                              )}
                            </p>
                            <span className="mb-0 text-uppercase fw-semibold">
                              {moment(item?.booked_start_time).format(
                                "hh:mm A",
                              )}
                            </span>
                          </td>
                          <td className="text-center">
                            ₹{item?.bookingwise_payment?.amount || 0}/-
                          </td>
                          <td className="text-center">
                            {item?.bookingwise_payment?.payment_method || "-"}
                          </td>
                          {canDelete && (
                            <td className="text-center">
                              {item?.status === "Confirmed" ? (
                                <div className="d-inline-flex gap-2">
                                  <TooltipComponent
                                    toolplace="top"
                                    tooltipText="Cancel"
                                  >
                                    <Button
                                      variant="btndelete_icon"
                                      className="btn_icon"
                                      onClick={() => {
                                        setIsUpdate(item)
                                        setCancellation(true)
                                      }}
                                    >
                                      <i className="icon_main cross_circle"></i>
                                    </Button>
                                  </TooltipComponent>
                                </div>
                              ) : (
                                "-"
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    {data && !data?.length && (
                      <tr>
                        <td className="text-center py-4" colSpan={9}>
                          <div className="norecord_wrapper">
                            <img src={recordNotFound} alt="No Record Found" />
                            <p className="norecords_title">No Records Found</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </InfiniteScroll>
            </Card.Body>
          </Card>
        ) : (
          <tr>
            <td className="text-center py-4" colSpan={9}>
              <div className="norecord_wrapper" style={{ paddingTop: "0px" }}>
                <img src={recordNotFound} alt="No Record Found" />
                <p className="norecords_title">No Records Found</p>
              </div>
            </td>
          </tr>
        )}
      </div>

      <BookingDetails show={show} handleClose={handleClose} isView={isView} />
      <BookingCancellation
        show={cancellation}
        setData={setBookingData}
        handleClose={handleClose}
        isUpdate={isUpdate}
        setIsUpdate={setIsUpdate}
        fetchData={fetchBookingdata}
      />
    </>
  ) : (
    <></>
  )
}

export default ChargingStationDetails
